<template>
  <aside class="st-rail side-list st-shell" :style="{ width: width + 'px' }">
    <ul v-if="items.length" class="st-rail__items">
      <li
        v-for="(item, index) in items"
        :key="index"
        class="st-rail__item side-list__item"
        :class="{
          active: index === selectedIndex,
          dragging: drag.index === index,
          'drop-target': over.index === index && drag.index !== index,
        }"
        draggable="true"
        @click="emit('select', index)"
        @dragstart="onDragStart(ROW_KEY, index, $event)"
        @dragover.prevent="onDragOver(ROW_KEY, index)"
        @drop="onDrop(ROW_KEY, index)"
        @dragend="onDragEnd"
      >
        <span
          class="st-rail__handle"
          title="Drag to reorder"
          @mousedown="dragArmed = true"
          @click.stop
        >
          <FontAwesomeIcon icon="grip-vertical" />
        </span>
        <span v-if="showIndex" class="side-list__index">{{ index + 1 }}</span>
        <span class="st-rail__label">
          <slot name="row" :item="item" :index="index" />
        </span>
      </li>
    </ul>
    <p v-else class="st-rail__empty">{{ emptyHint }}</p>

    <StButton class="side-list__add" on-shell block icon="plus" @click="emit('add')">
      {{ addLabel }}
    </StButton>

    <!-- Drag the right edge to resize the sidebar (width persisted per widthKey). -->
    <div
      class="st-rail__resize"
      :class="{ resizing }"
      title="Drag to resize"
      @mousedown="startResize"
    />
  </aside>
</template>

<script setup lang="ts" generic="T">
// The studio's reusable left list rail: a resizable, drag-to-reorder list of
// entries with an add button, shared by every surface that has a sidebar
// (battleplans, rules documents, battle tactics). Each surface supplies its own
// row label via the #row slot and owns add/select/reorder semantics; this
// component owns the shell chrome, resize, and drag-to-reorder so they stay
// identical everywhere. Shell/resize/drag visuals live in styles/rail.css; the
// resize and drag-to-reorder behaviour in the useRailResize / useDragReorder
// composables.
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { StButton } from './ui/index.js';
import { useRailResize } from '../composables/useRailResize.js';
import { useDragReorder } from '../composables/useDragReorder.js';

const props = defineProps<{
  items: T[];
  selectedIndex: number;
  addLabel: string;
  emptyHint: string;
  /** localStorage key the resized width is persisted under (unique per surface). */
  widthKey: string;
  /** Show a 1-based ordinal badge before each row (battleplans use this). */
  showIndex?: boolean;
}>();

const emit = defineEmits<{
  select: [index: number];
  add: [];
  reorder: [from: number, to: number];
}>();

defineSlots<{
  row(props: { item: T; index: number }): unknown;
}>();

const { width, resizing, startResize } = useRailResize(props.widthKey);

// A flat list is a single "section": every row shares one constant key.
const ROW_KEY = 0;
const { dragArmed, drag, over, onDragStart, onDragOver, onDrop, onDragEnd } =
  useDragReorder<number>((_key, from, to) => emit('reorder', from, to));
</script>

<style scoped>
.side-list {
  gap: var(--st-space-2);
}

/* 1-based ordinal badge (battleplans/rules sections). */
.side-list__index {
  flex-shrink: 0;
  min-width: 1.1rem;
  font-size: var(--st-text-xs);
  font-weight: var(--st-weight-bold);
  font-variant-numeric: tabular-nums;
  color: var(--st-accent-on-dark);
  text-align: right;
}

.side-list__add {
  margin-top: auto;
}
</style>
